import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Download,
  RefreshCw,
  FileSpreadsheet,
  Printer,
  IndianRupee,
  Receipt,
  CalendarDays,
  X,
  Filter,
} from "lucide-react";

import * as XLSX from "xlsx";

import api from "../constant/api";
import { toast } from "react-toastify";
import { useMenuPermission } from "../hooks/useMenuPermission";

// Change this if your menu permission ID is different
const MENU_ID = 24;

// ============================================================
// HELPERS
// ============================================================

const money = (value) => {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (date) => {
  if (!date) return "—";

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }

  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getMonthStart = () => {
  const now = new Date();

  return `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}-01`;
};

const getToday = () => {
  const now = new Date();

  return `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
};

// ============================================================
// COMPONENT
// ============================================================

export default function GSTReport() {
  const { loading: permissionLoading } =
    useMenuPermission(MENU_ID);

  // ==========================================================
  // STATES
  // ==========================================================

  const [reports, setReports] = useState([]);

  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");

  const [fromDate, setFromDate] = useState(
    getMonthStart()
  );

  const [toDate, setToDate] = useState(
    getToday()
  );

  const [branch, setBranch] = useState("");

  const [paymentMethod, setPaymentMethod] =
    useState("");

  // ==========================================================
  // LOAD GST REPORT
  // ==========================================================

  const loadGSTReport = async (
    showRefresh = false
  ) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params = new URLSearchParams();

      if (fromDate) {
        params.append(
          "from_date",
          fromDate
        );
      }

      if (toDate) {
        params.append(
          "to_date",
          toDate
        );
      }

      if (branch) {
        params.append(
          "branch",
          branch
        );
      }

      if (paymentMethod) {
        params.append(
          "payment_method",
          paymentMethod
        );
      }

      const res = await api.get(
        `/Salesman/getGSTReport?${params.toString()}`
      );

      console.log(
        "GST REPORT:",
        res.data
      );

      if (res.data?.success) {
        setReports(
          res.data.data || []
        );
      } else {
        setReports([]);

        toast.error(
          res.data?.msg ||
            "Failed to load GST report"
        );
      }
    } catch (error) {
      console.log(
        "GST Report Error:",
        error?.response?.data ||
          error
      );

      setReports([]);

      toast.error(
        error?.response?.data?.msg ||
          "Failed to load GST report"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    if (!permissionLoading) {
      loadGSTReport();
    }
  }, [permissionLoading]);

  // ==========================================================
  // FILTER REPORT
  // ==========================================================

  const filteredReports = useMemo(() => {
    const keyword =
      search
        .trim()
        .toLowerCase();

    if (!keyword) {
      return reports;
    }

    return reports.filter(
      (item) => {
        return (
          String(
            item.invoice_no || ""
          )
            .toLowerCase()
            .includes(keyword) ||

          String(
            item.customer_name || ""
          )
            .toLowerCase()
            .includes(keyword) ||

          String(
            item.phone || ""
          )
            .toLowerCase()
            .includes(keyword) ||

          String(
            item.gst_no || ""
          )
            .toLowerCase()
            .includes(keyword) ||

          String(
            item.branch || ""
          )
            .toLowerCase()
            .includes(keyword)
        );
      }
    );
  }, [reports, search]);

  // ==========================================================
  // TOTALS
  // ==========================================================

  const totals = useMemo(() => {
    return filteredReports.reduce(
      (acc, item) => {
        const taxable =
          Number(
            item.taxable_amount
          ) ||
          Number(
            item.total_amount
          ) ||
          0;

        const gst =
          Number(
            item.gst_amount
          ) || 0;

        const cgst =
          Number(
            item.cgst
          ) || gst / 2;

        const sgst =
          Number(
            item.sgst
          ) || gst / 2;

        const discount =
          Number(
            item.discount
          ) || 0;

        const net =
          Number(
            item.net_total
          ) ||
          taxable +
            gst -
            discount;

        acc.taxable += taxable;
        acc.gst += gst;
        acc.cgst += cgst;
        acc.sgst += sgst;
        acc.discount += discount;
        acc.net += net;

        return acc;
      },
      {
        taxable: 0,
        gst: 0,
        cgst: 0,
        sgst: 0,
        discount: 0,
        net: 0,
      }
    );
  }, [filteredReports]);

  // ==========================================================
  // CLEAR FILTERS
  // ==========================================================

  const clearFilters = () => {
    setSearch("");
    setBranch("");
    setPaymentMethod("");
    setFromDate(
      getMonthStart()
    );
    setToDate(
      getToday()
    );
  };

  // ==========================================================
  // BRANCH LIST
  // ==========================================================

  const branches = useMemo(() => {
    const values = reports
      .map(
        (item) =>
          item.branch
      )
      .filter(Boolean);

    return [
      ...new Set(values),
    ];
  }, [reports]);

  // ==========================================================
  // PAYMENT METHODS
  // ==========================================================

  const paymentMethods = useMemo(() => {
    const values = reports
      .map(
        (item) =>
          item.payment_method
      )
      .filter(Boolean);

    return [
      ...new Set(values),
    ];
  }, [reports]);

  // ==========================================================
  // EXCEL EXPORT
  // ==========================================================

  const exportExcel = () => {
    if (
      filteredReports.length ===
      0
    ) {
      toast.error(
        "No GST records to export"
      );

      return;
    }

    try {
      const excelData =
        filteredReports.map(
          (item, index) => {
            const taxable =
              Number(
                item.taxable_amount
              ) ||
              Number(
                item.total_amount
              ) ||
              0;

            const gst =
              Number(
                item.gst_amount
              ) || 0;

            const cgst =
              Number(
                item.cgst
              ) || gst / 2;

            const sgst =
              Number(
                item.sgst
              ) || gst / 2;

            const discount =
              Number(
                item.discount
              ) || 0;

            const net =
              Number(
                item.net_total
              ) ||
              taxable +
                gst -
                discount;

            return {
              "S.No": index + 1,

              "Invoice No":
                item.invoice_no ||
                "",

              "Invoice Date":
                formatDate(
                  item.created_at
                ),

              "Order ID":
                item.order_id ||
                "",

              "Customer Name":
                item.customer_name ||
                "",

              "Phone":
                item.phone ||
                "",

              "GSTIN":
                item.gst_no ||
                "",

              "City":
                item.city ||
                "",

              "Branch":
                item.branch ||
                "",

              "Payment Method":
                item.payment_method ||
                "",

              "Taxable Amount":
                Number(
                  taxable.toFixed(2)
                ),

              "Discount":
                Number(
                  discount.toFixed(2)
                ),

              "GST %":
                Number(
                  item.gst_percentage ||
                    18
                ),

              "GST Amount":
                Number(
                  gst.toFixed(2)
                ),

              "CGST":
                Number(
                  cgst.toFixed(2)
                ),

              "SGST":
                Number(
                  sgst.toFixed(2)
                ),

              "Net Total":
                Number(
                  net.toFixed(2)
                ),

              "Status":
                item.status ||
                "",
            };
          }
        );

      // ======================================================
      // TOTAL ROW
      // ======================================================

      excelData.push({
        "S.No": "",
        "Invoice No": "",
        "Invoice Date": "",
        "Order ID": "",
        "Customer Name": "TOTAL",
        "Phone": "",
        "GSTIN": "",
        "City": "",
        "Branch": "",
        "Payment Method": "",

        "Taxable Amount":
          Number(
            totals.taxable.toFixed(
              2
            )
          ),

        "Discount":
          Number(
            totals.discount.toFixed(
              2
            )
          ),

        "GST %": "",

        "GST Amount":
          Number(
            totals.gst.toFixed(
              2
            )
          ),

        "CGST":
          Number(
            totals.cgst.toFixed(
              2
            )
          ),

        "SGST":
          Number(
            totals.sgst.toFixed(
              2
            )
          ),

        "Net Total":
          Number(
            totals.net.toFixed(
              2
            )
          ),

        "Status": "",
      });

      const worksheet =
        XLSX.utils.json_to_sheet(
          excelData
        );

      // ======================================================
      // COLUMN WIDTH
      // ======================================================

      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 18 },
        { wch: 16 },
        { wch: 12 },
        { wch: 25 },
        { wch: 15 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 10 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "GST Report"
      );

      const fileName =
        `GST_Report_${fromDate || "all"}_to_${toDate || "all"}.xlsx`;

      XLSX.writeFile(
        workbook,
        fileName
      );

      toast.success(
        "GST Excel exported successfully"
      );
    } catch (error) {
      console.log(
        "Excel Export Error:",
        error
      );

      toast.error(
        "Excel export failed"
      );
    }
  };

  // ==========================================================
  // PRINT
  // ==========================================================

  const printReport = () => {
    window.print();
  };

  // ==========================================================
  // PERMISSION LOADING
  // ==========================================================

  if (permissionLoading) {
    return (
      <div className="p-6 text-gray-500">
        Checking permissions...
      </div>
    );
  }

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="space-y-5 animate-pulse">

        <div className="h-8 w-64 bg-gray-200 rounded" />

        <div className="h-4 w-80 bg-gray-200 rounded" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

          {Array.from({
            length: 4,
          }).map((_, i) => (
            <div
              key={i}
              className="
                h-32
                bg-gray-200
                rounded-2xl
              "
            />
          ))}

        </div>

        <div className="
          h-96
          bg-gray-200
          rounded-2xl
        " />

      </div>
    );
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="space-y-5">

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="
        flex
        flex-col
        md:flex-row
        md:items-center
        md:justify-between
        gap-3
        print:hidden
      ">

        <div>

          <h1 className="
            text-2xl
            md:text-3xl
            font-bold
            text-gray-800
          ">
            GST Report
          </h1>

          <p className="
            text-sm
            text-gray-500
            mt-1
          ">
            Invoice-wise GST sales report
          </p>

        </div>

        <div className="
          flex
          gap-2
          flex-wrap
        ">

          <button
            onClick={() =>
              loadGSTReport(true)
            }
            disabled={refreshing}
            className="
              flex
              items-center
              gap-2
              bg-gray-900
              hover:bg-gray-800
              text-white
              px-4
              py-2.5
              rounded-xl
              text-sm
              font-semibold
              disabled:opacity-50
            "
          >

            <RefreshCw
              size={16}
              className={
                refreshing
                  ? "animate-spin"
                  : ""
              }
            />

            Refresh

          </button>

          <button
            onClick={
              printReport
            }
            className="
              flex
              items-center
              gap-2
              bg-gray-100
              hover:bg-gray-200
              text-gray-700
              px-4
              py-2.5
              rounded-xl
              text-sm
              font-semibold
            "
          >

            <Printer size={16} />

            Print

          </button>

          <button
            onClick={
              exportExcel
            }
            className="
              flex
              items-center
              gap-2
              bg-green-600
              hover:bg-green-700
              text-white
              px-4
              py-2.5
              rounded-xl
              text-sm
              font-semibold
              shadow
            "
          >

            <FileSpreadsheet
              size={17}
            />

            Export Excel

          </button>

        </div>

      </div>

      {/* ======================================================
          FILTERS
      ====================================================== */}

      <div className="
        bg-white
        rounded-2xl
        border
        border-gray-100
        shadow-sm
        p-4
        print:hidden
      ">

        <div className="
          flex
          items-center
          gap-2
          mb-4
        ">

          <Filter
            size={18}
            className="text-blue-600"
          />

          <h2 className="
            font-bold
            text-gray-800
          ">
            Report Filters
          </h2>

        </div>

        <div className="
          grid
          grid-cols-1
          sm:grid-cols-2
          lg:grid-cols-5
          gap-3
        ">

          {/* SEARCH */}

          <div className="
            relative
            lg:col-span-2
          ">

            <Search
              size={17}
              className="
                absolute
                left-3
                top-1/2
                -translate-y-1/2
                text-gray-400
              "
            />

            <input
              type="text"
              placeholder="
                Search invoice, customer, GSTIN...
              "
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              className="
                w-full
                border
                border-gray-200
                rounded-xl
                pl-10
                pr-3
                py-2.5
                outline-none
                focus:ring-2
                focus:ring-blue-500
              "
            />

          </div>

          {/* FROM DATE */}

          <div>

            <label className="
              block
              text-xs
              font-semibold
              text-gray-500
              mb-1
            ">
              From Date
            </label>

            <input
              type="date"
              value={fromDate}
              onChange={(e) =>
                setFromDate(
                  e.target.value
                )
              }
              className="
                w-full
                border
                border-gray-200
                rounded-xl
                px-3
                py-2.5
                outline-none
                focus:ring-2
                focus:ring-blue-500
              "
            />

          </div>

          {/* TO DATE */}

          <div>

            <label className="
              block
              text-xs
              font-semibold
              text-gray-500
              mb-1
            ">
              To Date
            </label>

            <input
              type="date"
              value={toDate}
              onChange={(e) =>
                setToDate(
                  e.target.value
                )
              }
              className="
                w-full
                border
                border-gray-200
                rounded-xl
                px-3
                py-2.5
                outline-none
                focus:ring-2
                focus:ring-blue-500
              "
            />

          </div>

          {/* BRANCH */}

          <div>

            <label className="
              block
              text-xs
              font-semibold
              text-gray-500
              mb-1
            ">
              Branch
            </label>

            <select
              value={branch}
              onChange={(e) =>
                setBranch(
                  e.target.value
                )
              }
              className="
                w-full
                border
                border-gray-200
                rounded-xl
                px-3
                py-2.5
                outline-none
                bg-white
                focus:ring-2
                focus:ring-blue-500
              "
            >

              <option value="">
                All Branches
              </option>

              {branches.map(
                (item) => (
                  <option
                    key={item}
                    value={item}
                  >
                    {item}
                  </option>
                )
              )}

            </select>

          </div>

        </div>

        {/* SECOND FILTER ROW */}

        <div className="
          mt-3
          flex
          flex-col
          sm:flex-row
          gap-3
          justify-between
        ">

          <select
            value={paymentMethod}
            onChange={(e) =>
              setPaymentMethod(
                e.target.value
              )
            }
            className="
              border
              border-gray-200
              rounded-xl
              px-3
              py-2.5
              outline-none
              bg-white
              text-sm
              focus:ring-2
              focus:ring-blue-500
            "
          >

            <option value="">
              All Payment Methods
            </option>

            {paymentMethods.map(
              (item) => (
                <option
                  key={item}
                  value={item}
                >
                  {item}
                </option>
              )
            )}

          </select>

          <button
            onClick={
              clearFilters
            }
            className="
              flex
              items-center
              justify-center
              gap-2
              text-sm
              text-gray-600
              bg-gray-100
              hover:bg-gray-200
              px-4
              py-2.5
              rounded-xl
            "
          >

            <X size={16} />

            Clear Filters

          </button>

        </div>

      </div>

      {/* ======================================================
          SUMMARY CARDS
      ====================================================== */}

      <div className="
        grid
        grid-cols-2
        lg:grid-cols-4
        gap-3
        md:gap-4
      ">

        {/* INVOICES */}

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="
            bg-white
            border
            border-gray-100
            rounded-2xl
            shadow-sm
            p-4
          "
        >

          <div className="
            flex
            justify-between
            items-start
          ">

            <div>

              <p className="
                text-xs
                md:text-sm
                text-gray-500
              ">
                Total Invoices
              </p>

              <p className="
                text-2xl
                font-bold
                text-gray-800
                mt-1
              ">
                {filteredReports.length}
              </p>

            </div>

            <div className="
              w-10
              h-10
              rounded-xl
              bg-blue-100
              flex
              items-center
              justify-center
            ">

              <Receipt
                size={20}
                className="text-blue-600"
              />

            </div>

          </div>

        </motion.div>

        {/* TAXABLE */}

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="
            bg-white
            border
            border-gray-100
            rounded-2xl
            shadow-sm
            p-4
          "
        >

          <div className="
            flex
            justify-between
            items-start
          ">

            <div>

              <p className="
                text-xs
                md:text-sm
                text-gray-500
              ">
                Taxable Amount
              </p>

              <p className="
                text-lg
                md:text-2xl
                font-bold
                text-gray-800
                mt-1
              ">
                ₹{money(
                  totals.taxable
                )}
              </p>

            </div>

            <div className="
              w-10
              h-10
              rounded-xl
              bg-indigo-100
              flex
              items-center
              justify-center
            ">

              <IndianRupee
                size={20}
                className="text-indigo-600"
              />

            </div>

          </div>

        </motion.div>

        {/* GST */}

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="
            bg-white
            border
            border-gray-100
            rounded-2xl
            shadow-sm
            p-4
          "
        >

          <div className="
            flex
            justify-between
            items-start
          ">

            <div>

              <p className="
                text-xs
                md:text-sm
                text-gray-500
              ">
                Total GST
              </p>

              <p className="
                text-lg
                md:text-2xl
                font-bold
                text-green-600
                mt-1
              ">
                ₹{money(
                  totals.gst
                )}
              </p>

              <p className="
                text-[10px]
                text-gray-400
                mt-1
              ">
                CGST ₹{money(
                  totals.cgst
                )} + SGST ₹{money(
                  totals.sgst
                )}
              </p>

            </div>

            <div className="
              w-10
              h-10
              rounded-xl
              bg-green-100
              flex
              items-center
              justify-center
            ">

              <IndianRupee
                size={20}
                className="text-green-600"
              />

            </div>

          </div>

        </motion.div>

        {/* NET */}

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="
            bg-gradient-to-r
            from-blue-600
            to-indigo-600
            text-white
            rounded-2xl
            shadow-lg
            p-4
          "
        >

          <p className="
            text-xs
            text-white/70
          ">
            Net Total
          </p>

          <p className="
            text-xl
            md:text-2xl
            font-bold
            mt-1
          ">
            ₹{money(
              totals.net
            )}
          </p>

          <p className="
            text-[10px]
            text-white/60
            mt-1
          ">
            After GST & discount
          </p>

        </motion.div>

      </div>

      {/* ======================================================
          GST BREAKDOWN
      ====================================================== */}

      <div className="
        grid
        grid-cols-1
        md:grid-cols-3
        gap-3
      ">

        <div className="
          bg-white
          rounded-2xl
          border
          p-4
          shadow-sm
        ">

          <p className="
            text-xs
            text-gray-400
          ">
            CGST
          </p>

          <p className="
            text-xl
            font-bold
            text-blue-600
            mt-1
          ">
            ₹{money(
              totals.cgst
            )}
          </p>

        </div>

        <div className="
          bg-white
          rounded-2xl
          border
          p-4
          shadow-sm
        ">

          <p className="
            text-xs
            text-gray-400
          ">
            SGST
          </p>

          <p className="
            text-xl
            font-bold
            text-purple-600
            mt-1
          ">
            ₹{money(
              totals.sgst
            )}
          </p>

        </div>

        <div className="
          bg-white
          rounded-2xl
          border
          p-4
          shadow-sm
        ">

          <p className="
            text-xs
            text-gray-400
          ">
            Discount
          </p>

          <p className="
            text-xl
            font-bold
            text-orange-600
            mt-1
          ">
            ₹{money(
              totals.discount
            )}
          </p>

        </div>

      </div>

      {/* ======================================================
          REPORT TABLE
      ====================================================== */}

      <div
        id="gst-print-area"
        className="
          bg-white
          rounded-2xl
          border
          border-gray-100
          shadow-sm
          overflow-hidden
        "
      >

        {/* TABLE HEADER */}

        <div className="
          p-4
          md:p-5
          border-b
          border-gray-100
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-2
        ">

          <div>

            <h2 className="
              font-bold
              text-gray-800
            ">
              GST Invoice Report
            </h2>

            <p className="
              text-xs
              text-gray-400
              mt-1
            ">
              {fromDate
                ? formatDate(
                    fromDate
                  )
                : "All dates"}
              {" "}to{" "}
              {toDate
                ? formatDate(
                    toDate
                  )
                : "All dates"}
            </p>

          </div>

          <div className="
            text-xs
            text-gray-500
          ">

            {filteredReports.length} records

          </div>

        </div>

        {/* ====================================================
            DESKTOP TABLE
        ==================================================== */}

        <div className="
          hidden
          lg:block
          overflow-x-auto
        ">

          <table className="
            min-w-[1500px]
            w-full
            text-sm
          ">

            <thead className="
              bg-gray-900
              text-white
            ">

              <tr>

                <th className="
                  px-4
                  py-3
                  text-left
                ">
                  #
                </th>

                <th className="
                  px-4
                  py-3
                  text-left
                ">
                  Invoice
                </th>

                <th className="
                  px-4
                  py-3
                  text-left
                ">
                  Date
                </th>

                <th className="
                  px-4
                  py-3
                  text-left
                ">
                  Customer
                </th>

                <th className="
                  px-4
                  py-3
                  text-left
                ">
                  GSTIN
                </th>

                <th className="
                  px-4
                  py-3
                  text-left
                ">
                  Branch
                </th>

                <th className="
                  px-4
                  py-3
                  text-right
                ">
                  Taxable
                </th>

                <th className="
                  px-4
                  py-3
                  text-right
                ">
                  GST %
                </th>

                <th className="
                  px-4
                  py-3
                  text-right
                ">
                  GST
                </th>

                <th className="
                  px-4
                  py-3
                  text-right
                ">
                  CGST
                </th>

                <th className="
                  px-4
                  py-3
                  text-right
                ">
                  SGST
                </th>

                <th className="
                  px-4
                  py-3
                  text-right
                ">
                  Discount
                </th>

                <th className="
                  px-4
                  py-3
                  text-right
                ">
                  Net Total
                </th>

                <th className="
                  px-4
                  py-3
                  text-center
                ">
                  Payment
                </th>

                <th className="
                  px-4
                  py-3
                  text-center
                ">
                  Status
                </th>

              </tr>

            </thead>

            <tbody>

              {filteredReports.map(
                (item, index) => {

                  const taxable =
                    Number(
                      item.taxable_amount
                    ) ||
                    Number(
                      item.total_amount
                    ) ||
                    0;

                  const gst =
                    Number(
                      item.gst_amount
                    ) || 0;

                  const cgst =
                    Number(
                      item.cgst
                    ) ||
                    gst / 2;

                  const sgst =
                    Number(
                      item.sgst
                    ) ||
                    gst / 2;

                  const discount =
                    Number(
                      item.discount
                    ) || 0;

                  const net =
                    Number(
                      item.net_total
                    ) ||
                    taxable +
                      gst -
                      discount;

                  return (
                    <tr
                      key={
                        item.invoice_id ||
                        item.invoice_no ||
                        index
                      }
                      className="
                        border-b
                        border-gray-100
                        hover:bg-gray-50
                      "
                    >

                      <td className="
                        px-4
                        py-3
                        text-gray-400
                      ">
                        {index + 1}
                      </td>

                      <td className="
                        px-4
                        py-3
                        font-semibold
                        text-blue-600
                      ">
                        {item.invoice_no ||
                          `INV-${item.invoice_id || ""}`}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-xs
                        text-gray-500
                      ">
                        {formatDate(
                          item.created_at
                        )}
                      </td>

                      <td className="
                        px-4
                        py-3
                      ">

                        <p className="
                          font-medium
                          text-gray-800
                        ">
                          {item.customer_name ||
                            "Customer"}
                        </p>

                        <p className="
                          text-xs
                          text-gray-400
                        ">
                          {item.phone ||
                            ""}
                        </p>

                      </td>

                      <td className="
                        px-4
                        py-3
                        text-xs
                        font-mono
                      ">
                        {item.gst_no ||
                          "—"}
                      </td>

                      <td className="
                        px-4
                        py-3
                      ">
                        {item.branch ||
                          "—"}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-right
                        font-medium
                      ">
                        ₹{money(
                          taxable
                        )}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-right
                      ">
                        {item.gst_percentage ||
                          18}%
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-right
                        font-semibold
                        text-green-600
                      ">
                        ₹{money(
                          gst
                        )}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-right
                        text-blue-600
                      ">
                        ₹{money(
                          cgst
                        )}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-right
                        text-purple-600
                      ">
                        ₹{money(
                          sgst
                        )}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-right
                        text-orange-600
                      ">
                        ₹{money(
                          discount
                        )}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-right
                        font-bold
                        text-gray-800
                      ">
                        ₹{money(
                          net
                        )}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-center
                        text-xs
                        capitalize
                      ">
                        {item.payment_method ||
                          "—"}
                      </td>

                      <td className="
                        px-4
                        py-3
                        text-center
                      ">

                        <span className="
                          px-2
                          py-1
                          rounded-full
                          bg-green-50
                          text-green-600
                          text-xs
                          font-semibold
                          capitalize
                        ">
                          {item.status ||
                            "Completed"}
                        </span>

                      </td>

                    </tr>
                  );
                }
              )}

              {/* TOTAL */}

              {filteredReports.length >
                0 && (
                <tr className="
                  bg-gray-100
                  font-bold
                ">

                  <td
                    colSpan={6}
                    className="
                      px-4
                      py-4
                      text-right
                    "
                  >
                    TOTAL
                  </td>

                  <td className="
                    px-4
                    py-4
                    text-right
                  ">
                    ₹{money(
                      totals.taxable
                    )}
                  </td>

                  <td />

                  <td className="
                    px-4
                    py-4
                    text-right
                    text-green-600
                  ">
                    ₹{money(
                      totals.gst
                    )}
                  </td>

                  <td className="
                    px-4
                    py-4
                    text-right
                    text-blue-600
                  ">
                    ₹{money(
                      totals.cgst
                    )}
                  </td>

                  <td className="
                    px-4
                    py-4
                    text-right
                    text-purple-600
                  ">
                    ₹{money(
                      totals.sgst
                    )}
                  </td>

                  <td className="
                    px-4
                    py-4
                    text-right
                    text-orange-600
                  ">
                    ₹{money(
                      totals.discount
                    )}
                  </td>

                  <td className="
                    px-4
                    py-4
                    text-right
                    text-blue-700
                  ">
                    ₹{money(
                      totals.net
                    )}
                  </td>

                  <td colSpan={2} />

                </tr>
              )}

            </tbody>

          </table>

        </div>

        {/* ====================================================
            MOBILE / TABLET CARDS
        ==================================================== */}

        <div className="
          lg:hidden
          divide-y
        ">

          {filteredReports.map(
            (item, index) => {

              const taxable =
                Number(
                  item.taxable_amount
                ) ||
                Number(
                  item.total_amount
                ) ||
                0;

              const gst =
                Number(
                  item.gst_amount
                ) || 0;

              const cgst =
                Number(
                  item.cgst
                ) ||
                gst / 2;

              const sgst =
                Number(
                  item.sgst
                ) ||
                gst / 2;

              const discount =
                Number(
                  item.discount
                ) || 0;

              const net =
                Number(
                  item.net_total
                ) ||
                taxable +
                  gst -
                  discount;

              return (
                <div
                  key={
                    item.invoice_id ||
                    item.invoice_no ||
                    index
                  }
                  className="p-4"
                >

                  <div className="
                    flex
                    items-start
                    justify-between
                    gap-3
                  ">

                    <div>

                      <p className="
                        font-bold
                        text-blue-600
                      ">
                        {item.invoice_no ||
                          `INV-${item.invoice_id || ""}`}
                      </p>

                      <p className="
                        text-sm
                        font-semibold
                        text-gray-800
                        mt-1
                      ">
                        {item.customer_name ||
                          "Customer"}
                      </p>

                      <p className="
                        text-xs
                        text-gray-400
                      ">
                        {item.phone ||
                          ""}
                      </p>

                    </div>

                    <span className="
                      px-2
                      py-1
                      rounded-full
                      bg-green-50
                      text-green-600
                      text-[10px]
                      font-semibold
                      capitalize
                    ">
                      {item.status ||
                        "Completed"}
                    </span>

                  </div>

                  <div className="
                    grid
                    grid-cols-2
                    gap-2
                    mt-4
                  ">

                    <div className="
                      bg-gray-50
                      rounded-xl
                      p-3
                    ">

                      <p className="
                        text-[10px]
                        text-gray-400
                      ">
                        Invoice Date
                      </p>

                      <p className="
                        text-xs
                        font-semibold
                        mt-1
                      ">
                        {formatDate(
                          item.created_at
                        )}
                      </p>

                    </div>

                    <div className="
                      bg-gray-50
                      rounded-xl
                      p-3
                    ">

                      <p className="
                        text-[10px]
                        text-gray-400
                      ">
                        GSTIN
                      </p>

                      <p className="
                        text-xs
                        font-semibold
                        mt-1
                        break-all
                      ">
                        {item.gst_no ||
                          "—"}
                      </p>

                    </div>

                    <div className="
                      bg-blue-50
                      rounded-xl
                      p-3
                    ">

                      <p className="
                        text-[10px]
                        text-blue-500
                      ">
                        Taxable
                      </p>

                      <p className="
                        text-sm
                        font-bold
                        text-blue-700
                        mt-1
                      ">
                        ₹{money(
                          taxable
                        )}
                      </p>

                    </div>

                    <div className="
                      bg-green-50
                      rounded-xl
                      p-3
                    ">

                      <p className="
                        text-[10px]
                        text-green-500
                      ">
                        GST
                      </p>

                      <p className="
                        text-sm
                        font-bold
                        text-green-700
                        mt-1
                      ">
                        ₹{money(
                          gst
                        )}
                      </p>

                    </div>

                    <div className="
                      bg-indigo-50
                      rounded-xl
                      p-3
                    ">

                      <p className="
                        text-[10px]
                        text-indigo-500
                      ">
                        CGST
                      </p>

                      <p className="
                        text-sm
                        font-bold
                        text-indigo-700
                        mt-1
                      ">
                        ₹{money(
                          cgst
                        )}
                      </p>

                    </div>

                    <div className="
                      bg-purple-50
                      rounded-xl
                      p-3
                    ">

                      <p className="
                        text-[10px]
                        text-purple-500
                      ">
                        SGST
                      </p>

                      <p className="
                        text-sm
                        font-bold
                        text-purple-700
                        mt-1
                      ">
                        ₹{money(
                          sgst
                        )}
                      </p>

                    </div>

                  </div>

                  <div className="
                    flex
                    items-center
                    justify-between
                    mt-4
                    pt-3
                    border-t
                  ">

                    <div>

                      <p className="
                        text-[10px]
                        text-gray-400
                      ">
                        Payment
                      </p>

                      <p className="
                        text-xs
                        font-semibold
                        capitalize
                      ">
                        {item.payment_method ||
                          "—"}
                      </p>

                    </div>

                    <div className="
                      text-right
                    ">

                      <p className="
                        text-[10px]
                        text-gray-400
                      ">
                        Net Total
                      </p>

                      <p className="
                        text-lg
                        font-bold
                        text-blue-600
                      ">
                        ₹{money(
                          net
                        )}
                      </p>

                    </div>

                  </div>

                </div>
              );
            }
          )}

          {filteredReports.length ===
            0 && (
            <div className="
              text-center
              py-14
              text-gray-400
            ">

              <Receipt
                size={32}
                className="
                  mx-auto
                  mb-2
                "
              />

              No GST records found.

            </div>
          )}

        </div>

      </div>

      {/* ======================================================
          PRINT FOOTER
      ====================================================== */}

      <div className="
        hidden
        print:block
        text-center
        text-xs
        text-gray-500
        mt-5
      ">

        GST Report generated on{" "}
        {new Date().toLocaleString(
          "en-IN"
        )}

      </div>

    </div>
  );
}